import React, { useLayoutEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'

import { tryOpenBankModal } from '../../utils/bankModalOpener'
import TailwindModal from '../ui/TailwindModal'
import { highlightCost } from '../modalCostHighlight'

// Shared palette first — DepositAlert.css reads its tokens from :root, keeping
// this in step with the confirm modal and the notification/alert modals.
import '../modalTheme.css'
import './DepositAlert.css'

/** Must match the exit animation duration in DepositAlert.css. */
const EXIT_MS = 140

interface DepositAlertState {
  visible: boolean
  text: string
  resolve?: () => void
}

let showDepositAlert: ((text: string) => Promise<void>) | undefined

export const DepositAlertHost: React.FC = () => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const [alertState, setAlertState] = useState<DepositAlertState>({
    visible: false,
    text: '',
  })

  useLayoutEffect(() => {
    showDepositAlert = (text: string) =>
      new Promise<void>((resolve) => {
        setAlertState({ visible: true, text, resolve })
      })

    return () => {
      showDepositAlert = undefined
    }
  }, [])

  const handleClose = () => {
    setAlertState((previousState) => {
      previousState.resolve?.()
      // Keep `text` so the message stays put while the modal animates out.
      return { ...previousState, visible: false, resolve: undefined }
    })
  }

  const handleDeposit = () => {
    handleClose()
    if (!tryOpenBankModal()) {
      navigate('/bank')
    }
  }

  return (
    <TailwindModal
      open={alertState.visible}
      onClose={handleClose}
      zIndex={9999}
      exitMs={EXIT_MS}
      aria-labelledby="deposit-alert-title"
      aria-describedby="deposit-alert-desc"
      backdropClassName="deposit-alert-backdrop"
    >
      <section className="deposit-alert-modal" role="dialog" aria-modal="true">
        <header className="deposit-alert-modal__header">
          <h2 id="deposit-alert-title" className="deposit-alert-modal__title">
            {t('alert.insufficientUsdtTitle')}
          </h2>

          <button
            className="deposit-alert-modal__close"
            type="button"
            aria-label={t('common.close', { defaultValue: 'Close' })}
            onClick={handleClose}
          />
        </header>

        <div className="deposit-alert-modal__content">
          <p id="deposit-alert-desc" className="deposit-alert-modal__message">
            {highlightCost(alertState.text, 'deposit-alert-modal__cost')}
          </p>
        </div>

        <div className="deposit-alert-modal__ornament" aria-hidden="true">
          <span className="deposit-alert-modal__ornament-line" />
          <span className="deposit-alert-modal__ornament-center">
            <i className="deposit-alert-modal__ornament-dot deposit-alert-modal__ornament-dot--left" />
            <i className="deposit-alert-modal__ornament-diamond deposit-alert-modal__ornament-diamond--left-outer" />
            <i className="deposit-alert-modal__ornament-diamond deposit-alert-modal__ornament-diamond--left-inner" />
            <i className="deposit-alert-modal__ornament-diamond deposit-alert-modal__ornament-diamond--main" />
            <i className="deposit-alert-modal__ornament-diamond deposit-alert-modal__ornament-diamond--right-inner" />
            <i className="deposit-alert-modal__ornament-diamond deposit-alert-modal__ornament-diamond--right-outer" />
            <i className="deposit-alert-modal__ornament-dot deposit-alert-modal__ornament-dot--right" />
          </span>
          <span className="deposit-alert-modal__ornament-line" />
        </div>

        <footer className="deposit-alert-modal__actions">
          <button
            className="deposit-alert-modal__button deposit-alert-modal__button--cancel"
            type="button"
            onClick={handleClose}
          >
            {t('common.ok')}
          </button>
          <button
            className="deposit-alert-modal__button"
            type="button"
            onClick={handleDeposit}
            autoFocus
          >
            {t('alert.deposit')}
          </button>
        </footer>

        <p className="deposit-alert-modal__hint">{t('alert.insufficientUsdtDepositHint')}</p>
      </section>
    </TailwindModal>
  )
}

export const depositAlert = {
  open: async (text: string) => {
    if (!showDepositAlert) {
      console.warn('Deposit alert not initialized yet.')
      return
    }
    await showDepositAlert(text)
  },
}
